import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { StarIcon, FileTextIcon, ZapIcon, Package2Icon, ShieldCheckIcon, HeartHandshakeIcon, GiftIcon, MailIcon, SettingsIcon, CheckCircle2Icon, ShoppingCartIcon } from "lucide-react";
import { PRODUCT_IMAGE, OFFICE_BG, BONUS_IMAGES, PREVIEW_IMAGES, GMAIL_REVIEWS, PROBLEMS, MODULES, BONUSES, TESTIMONIALS, OBJECTIONS, SUPPORT_EMAIL, META_PIXEL_ID, scrollToBuy } from "./lib/constants";
import { BuyButton } from "./components/BuyButton";
import { Accordion } from "./components/Accordion";
import { ReviewCarousel } from "./components/ReviewCarousel";
import { PreviewCarousel } from "./components/PreviewCarousel";
import { BuyerPopup } from "./components/BuyerPopup";
import { CountdownUrgency } from "./components/CountdownUrgency";
import { ComparisonTable } from "./components/ComparisonTable";
function Stars({
  className = ""
}: {
  className?: string;
}) {
  return <div className={`flex items-center gap-0.5 ${className}`} data-id="element-0" data-mp="3ik7gwiph6frp">
      {Array.from({
      length: 5
    }).map((_, i) => <StarIcon key={i} className="h-5 w-5 fill-[#f5b400] text-[#f5b400]" data-id="element-1" data-mp="3ml12h5fg3ihf" data-mpi={i} />)}
    </div>;
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
function Section({
  children,
  className = ""
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section variants={fadeUp} initial="hidden" whileInView="show" viewport={{
    once: true,
    amount: 0.15
  }} className={`px-4 ${className}`} data-id="element-2" data-mp="2zmo5j8fjol24">
      {children}
    </motion.section>;
}
export function App() {
  // Meta Pixel (PageView)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if ((window as any).fbq) return;

    /* eslint-disable */
    (function (f: any, b, e, v, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      if (s?.parentNode) {
        s.parentNode.insertBefore(t, s);
      } else {
        b.head.appendChild(t);
      }
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */

    (window as any).fbq("init", META_PIXEL_ID);
    (window as any).fbq("track", "PageView");
  }, []);
  return <div className="relative min-h-full w-full bg-[#f6f4ec] text-slate-800" data-id="element-3" data-mp="3lkhfm5in9do7">
      {/* Blurred HR office background */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0 bg-cover bg-center" style={{
      backgroundImage: `url(${OFFICE_BG})`
    }} data-id="element-4" data-mp="2xuddy3p0nnzu" />
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0 bg-[#f6f4ec]/85 backdrop-blur-md" data-id="element-5" data-mp="18driltpr7bcr" />

      <BuyerPopup data-id="element-6" data-mp="1d4rdyuj85ld5" />

      {/* Sticky mini CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#1f4d3a]/10 bg-white/95 p-2.5 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] backdrop-blur sm:hidden" data-id="element-7" data-mp="1d6ko4jt1qnpw">
        <button onClick={scrollToBuy} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1f4d3a] px-4 py-3 text-sm font-extrabold text-white" data-id="element-8" data-mp="13fgabnf3x8wn">
          <ShoppingCartIcon className="h-4 w-4" data-id="element-9" data-mp="1w211onvl1l07" />
          Quiero mi Kit — $17
        </button>
      </div>

      <main className="relative z-10 mx-auto max-w-md pb-24" data-id="element-10" data-mp="1i319j68v2nza">
        {/* ===================== HERO ===================== */}
        <Section className="pt-8 text-center" data-id="element-11" data-mp="1m70ohkehteqj">
          <span className="mb-3 inline-block rounded-full bg-[#1f4d3a] px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#f4e3b6]" data-id="element-12" data-mp="6ncmyfzirqz0">
            🇨🇱 Kit RRHH en Orden · Chile
          </span>
          <h1 className="mx-auto max-w-[23rem] text-[22px] font-black leading-[1.08] tracking-[-0.035em] text-[#1f4d3a] sm:max-w-none sm:text-3xl" data-id="element-13" data-mp="qy714ulemp5e">
            Reducí errores administrativos y riesgos laborales sin contratar consultores ni hacer cursos interminables.
          </h1>

          <button onClick={scrollToBuy} className="mt-5 block w-full" aria-label="Ver la oferta del Kit RRHH" data-id="element-14" data-mp="c90wus7pm8qn">
            <img src={PRODUCT_IMAGE} alt="Kit RRHH en Orden Chile — herramientas Excel, guías y modelos" className="w-full rounded-2xl border border-[#1f4d3a]/10 shadow-xl transition-transform hover:scale-[1.01]" data-id="element-15" data-mp="19tnf2zrurog8" />
          </button>

          <p className="mt-5 text-[15px] font-medium leading-relaxed text-slate-600" data-id="element-16" data-mp="vybbs3zmspnp">
            Creado para quienes hacen RRHH en una PYME sin equipo, sin tiempo y
            con la responsabilidad de que todo salga bien.
          </p>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-2" data-id="element-17" data-mp="1w4yk20vebtzi">
            {[{
            icon: <FileTextIcon className="h-4 w-4" data-id="element-18" data-mp="2058rlhyu6sgh" />,
            t: "100% PDF"
          }, {
            icon: <ZapIcon className="h-4 w-4" data-id="element-19" data-mp="255d8va7kjr7r" />,
            t: "Entrega Inmediata"
          }, {
            icon: <Package2Icon className="h-4 w-4" data-id="element-20" data-mp="29vtmaube3cu4" />,
            t: "20 herramientas listas"
          }].map(c => <span key={c.t} className="flex items-center gap-1.5 rounded-full border border-[#1f4d3a]/15 bg-white px-3 py-1.5 text-[12px] font-semibold text-[#1f4d3a]" data-id="element-21" data-mp="2etv7bksdlm88">
                {c.icon}
                {c.t}
              </span>)}
          </div>

          <div className="mt-6" data-id="element-22" data-mp="2su5xellv2wsc">
            <BuyButton data-id="element-23" data-mp="thlb1vw2kpxs" />
          </div>
        </Section>

        {/* ===================== GMAIL REVIEWS ===================== */}
        <Section className="mt-12" data-id="element-24" data-mp="yfv86c1cu0m1">
          <div className="mb-4 flex flex-col items-center" data-id="element-25" data-mp="ygwvpfzk4fkn">
            <Stars data-id="element-26" data-mp="21qskmeiwm8jd" />
            <div className="mt-1 flex items-center gap-2" data-id="element-27" data-mp="21sdgc7gyfxit">
              <p className="text-lg font-black text-[#1f4d3a]" data-id="element-28" data-mp="1wsr1xupe4b8f">4.9/5</p>
              <span className="h-1 w-1 rounded-full bg-[#1f4d3a]/30" data-id="element-29" data-mp="1wuk715kcuneb" />
              <p className="text-sm font-bold text-[#2f6b4f]" data-id="element-30" data-mp="2attpkbw19tlg">
                +300 profesionales
              </p>
            </div>
            <p className="text-xs text-slate-500" data-id="element-31" data-mp="25vie5wq3bb14">
              Basado en cientos de encargadas de RRHH en Chile
            </p>
          </div>
          <ReviewCarousel reviews={GMAIL_REVIEWS} data-id="element-32" data-mp="2fqadp638g7yj" />
        </Section>

        {/* ===================== PROBLEMS ===================== */}
        <Section className="mt-12" data-id="element-33" data-mp="2tkum6t9old1u">
          <h2 className="text-center text-xl font-black leading-snug text-[#1f4d3a]" data-id="element-34" data-mp="2pheodtlvl6nk">
            Sabemos lo complicado que puede ser RRHH estando sola
          </h2>
          <ul className="mt-5 flex flex-col gap-2.5" data-id="element-35" data-mp="33etnjq1xkssq">
            {PROBLEMS.map((p, i) => <li key={i} className="flex items-start gap-2.5 rounded-xl border border-red-100 bg-red-50/70 px-3 py-3 text-sm text-slate-700" data-id="element-36" data-mp="2yfqg5yyhaahl" data-mpi={i}>
                <span className="mt-0.5 text-base" data-id="element-37" data-mp="3n1y5uyz8h5oh">❌</span>
                <span data-id="element-38" data-mp="3868esmf6y2cw">{p}</span>
              </li>)}
          </ul>
        </Section>

        {/* ===================== MODULES ===================== */}
        <Section className="mt-10" data-id="element-39" data-mp="2zd576ur88jq6">
          <div className="mb-4 text-center" data-id="element-40" data-mp="2zcvirbltykml">
            <p className="inline-block rounded-full bg-[#1f4d3a] px-4 py-1.5 text-sm font-bold text-white" data-id="element-41" data-mp="2zb28znfx7kad">
              Para eso, creamos este kit
            </p>
          </div>
          <Accordion items={MODULES} data-id="element-42" data-mp="2pgioabigo2qs" />
        </Section>

        {/* ===================== PRODUCT PREVIEWS ===================== */}
        <Section className="mt-12" data-id="element-43" data-mp="2ge9e5mr3ubdx">
          <h2 className="mb-4 text-center text-lg font-black leading-snug text-[#1f4d3a]" data-id="element-44" data-mp="2ge12tzamre4u">
            Mira una parte de lo que incluye este kit
          </h2>
          <PreviewCarousel previews={PREVIEW_IMAGES} data-id="element-45" data-mp="30vdk29jfqz4l" />
        </Section>

        {/* ===================== BONUSES ===================== */}
        <Section className="mt-12" data-id="element-46" data-mp="2vxc0zom0ezt4">
          <div className="mb-4 text-center" data-id="element-47" data-mp="2vu7ps7a4n3sy">
            <h2 className="text-lg font-black leading-snug text-[#1f4d3a]" data-id="element-48" data-mp="2qwo9z2xs6iyw">
              Sabemos que solo con esto, no podés solucionar del todo RRHH, por
              eso agregamos los siguientes bonos:
            </h2>
            <span className="mt-2 inline-block rounded-full bg-[#c7a24a] px-3 py-1 text-[11px] font-black uppercase tracking-wide text-[#1f2d24]" data-id="element-49" data-mp="2hxb1odle80fr">
              🎁 Gratis solo por hoy
            </span>
          </div>
          <Accordion items={BONUSES.map(b => ({
          ...b,
          img: BONUS_IMAGES[b.img]
        }))} defaultOpen={null} data-id="element-50" data-mp="286zkvqgm1wrg" />
        </Section>

        {/* ===================== COMPARISON ===================== */}
        <Section className="mt-12" data-id="element-51" data-mp="1y80nx557jpka">
          <h2 className="mb-4 text-center text-xl font-black text-[#1f4d3a]" data-id="element-52" data-mp="1u6x5cavrzffd">
            Sin Kit RRHH vs Con Kit RRHH
          </h2>
          <ComparisonTable data-id="element-53" data-mp="9nurd9oibvog" />
        </Section>

        {/* ===================== SHORT TESTIMONIALS ===================== */}
        <Section className="mt-12" data-id="element-54" data-mp="dowtby9t6lu4">
          <div className="mb-4 flex items-center justify-center gap-2 text-[#1f4d3a]" data-id="element-55" data-mp="ipktb9g0md0h">
            <HeartHandshakeIcon className="h-5 w-5" data-id="element-56" data-mp="inrnfwd87wtj" />
            <h2 className="text-lg font-black" data-id="element-57" data-mp="nnm99li59w7q">Lo que dicen quienes ya lo usan</h2>
          </div>
          <div className="flex flex-col gap-3" data-id="element-58" data-mp="3m7cjqjei6owk">
            {TESTIMONIALS.map(t => <div key={t.name} className="rounded-2xl border border-[#1f4d3a]/10 bg-white/95 p-4 shadow-sm" data-id="element-59" data-mp="3r4mbw6ta1eo3">
                <div className="flex items-center gap-3" data-id="element-60" data-mp="3w2o0165k846f">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#1f4d3a] text-sm font-bold text-white" data-id="element-61" data-mp="4ql2pw8xzo1o">
                    {t.name.charAt(0)}
                  </div>
                  <div data-id="element-62" data-mp="38dbrm8i24056">
                    <p className="text-sm font-bold text-[#1f4d3a]" data-id="element-63" data-mp="38cju2s4wfsc9">
                      {t.name}, {t.age}
                    </p>
                    <p className="text-[11px] text-slate-500" data-id="element-64" data-mp="2k5ou0w68194e">{t.role}</p>
                  </div>
                  <Stars className="ml-auto [&_svg]:h-3.5 [&_svg]:w-3.5" data-id="element-65" data-mp="2p4rr9ufymb1t" />
                </div>
                <p className="mt-2 text-sm leading-relaxed text-slate-700" data-id="element-66" data-mp="2u4xmfklfufon">
                  “{t.text}”
                </p>
              </div>)}
          </div>
        </Section>

        {/* ===================== FINAL OFFER ===================== */}
        <Section id="compra" className="mt-14 scroll-mt-4" data-id="element-67" data-mp="21dmclilr9ot3">
          <div className="rounded-3xl border-2 border-[#c7a24a] bg-white p-5 shadow-2xl" data-id="element-68" data-mp="26ds510zunfc8">
            <div className="mb-4 text-center" data-id="element-69" data-mp="2b5s4ksc0c68a">
              <span className="inline-block rounded-full bg-[#1f4d3a] px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#f4e3b6]" data-id="element-70" data-mp="2b7moc3bjlsas">
                Oferta por tiempo limitado
              </span>
              <h2 className="mt-3 text-2xl font-black text-[#1f4d3a]" data-id="element-71" data-mp="1imw8jqkg7vyn">
                Kit RRHH en Orden — Chile
              </h2>
            </div>

            <img src={PRODUCT_IMAGE} alt="Kit RRHH en Orden Chile" className="mx-auto mb-4 w-40 rounded-xl shadow-md" data-id="element-72" data-mp="3ryyi4nzrydxi" />

            {/* Countdown + cupos */}
            <CountdownUrgency data-id="element-73" data-mp="3hz7ospmya4z0" />

            {/* Order summary */}
            <div className="mt-5 rounded-2xl border border-slate-200 bg-[#f9f8f2] p-4" data-id="element-74" data-mp="3d0wfqh4hbyhu">
              <p className="mb-3 text-sm font-black uppercase tracking-wide text-[#1f4d3a]" data-id="element-75" data-mp="389eedryjnvwc">
                Resumen de tu pedido
              </p>
              <ul className="flex flex-col gap-2 text-[13px] text-slate-700" data-id="element-76" data-mp="348ukqyd6jaey">
                {["5 Herramientas Core en Excel (Ficha, Feriado, Gratificación, Remuneraciones, Asistencia)", "1 Guía General de RRHH Chile (PDF)", "Bono: Finiquito (Excel)", "Bono: Guía de Contratos de Trabajo (PDF)", "Bono: Checklist + Onboarding (PDF + Excel)", "Bono: Modelos de Escritos (3 Word)", "Bono: Guion de Conversaciones Difíciles (Word)", "Bono: Reglamento Interno de Orden, Higiene y Seguridad", "Bono: Protocolo Ley Karin"].map(item => <li key={item} className="flex items-start gap-2" data-id="element-77" data-mp="2aqfxasu6ma17">
                    <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#2f6b4f]" data-id="element-78" data-mp="2yf8wqza0j00k" />
                    <span data-id="element-79" data-mp="33bphrklx0y9f">{item}</span>
                  </li>)}
              </ul>

              <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3" data-id="element-80" data-mp="2pkuh2vlklngx">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-[#c7a24a]" data-id="element-81" data-mp="2tlwh3enba3jy">
                  <GiftIcon className="h-4 w-4" data-id="element-82" data-mp="3hc0ayuap6oxr" /> Bonos gratis hoy
                </span>
                <span className="text-sm font-bold text-[#2f6b4f]" data-id="element-83" data-mp="3m4jxigrd4uep">Incluidos</span>
              </div>

              <div className="mt-4 flex items-end justify-center gap-3" data-id="element-84" data-mp="1anhv9povgry3">
                <span className="text-lg font-semibold text-slate-400 line-through" data-id="element-85" data-mp="15pzskh1g78yj">
                  $40
                </span>
                <span className="text-5xl font-black text-[#1f4d3a]" data-id="element-86" data-mp="1kd1dct4rghrw">$17</span>
                <span className="mb-1 text-sm font-semibold text-slate-500" data-id="element-87" data-mp="1fezv2af5smhh">
                  USD
                </span>
              </div>
              <p className="text-center text-xs text-slate-500" data-id="element-88" data-mp="rpxdt3ofsvni">
                Pago único · Acceso inmediato
              </p>
            </div>

            <div className="mt-5" data-id="element-89" data-mp="11oe9uf28g0gf">
              <BuyButton label="Quiero mi Kit RRHH ahora" onClickBuy data-id="element-90" data-mp="11pfw7fop89dg" />
            </div>

            <div className="mt-3 flex items-center justify-center gap-4 text-[11px] text-slate-500" data-id="element-91" data-mp="wnywhnhyasyg">
              <span className="flex items-center gap-1" data-id="element-92" data-mp="2d1p0yvczfj8m">
                <ShieldCheckIcon className="h-4 w-4 text-[#2f6b4f]" data-id="element-93" data-mp="284725xsoikec" /> Compra
                segura
              </span>
              <span className="flex items-center gap-1" data-id="element-94" data-mp="hnlcov73ze53">
                <ZapIcon className="h-4 w-4 text-[#2f6b4f]" data-id="element-95" data-mp="hp64we5yakzs" /> Entrega inmediata
              </span>
            </div>
          </div>
        </Section>

        {/* ===================== OBJECTIONS ===================== */}
        <Section className="mt-12" data-id="element-96" data-mp="vmco29ul6lpj">
          <h2 className="mb-4 text-center text-lg font-black text-[#1f4d3a]" data-id="element-97" data-mp="vndz4eblr879">
            Preguntas frecuentes
          </h2>
          <Accordion items={OBJECTIONS.map(o => ({
          title: o.q,
          body: o.a
        }))} defaultOpen={null} data-id="element-98" data-mp="10jl68afqipqs" />
        </Section>

        {/* ===================== SUPPORT ===================== */}
        <Section className="mt-10 pb-4 text-center" data-id="element-99" data-mp="1ed2ozt0ljyfr">
          <div className="inline-flex flex-col items-center gap-2 rounded-2xl border border-[#1f4d3a]/10 bg-white/90 px-6 py-5" data-id="element-100" data-mp="1ebsqj2pu0o8l">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#e7f0ea]" data-id="element-101" data-mp="1ja2mvgnaluvs">
              <SettingsIcon className="h-6 w-6 text-[#1f4d3a]" data-id="element-102" data-mp="1o95mumof1aww" />
            </div>
            <p className="text-sm font-semibold text-[#1f4d3a]" data-id="element-103" data-mp="1ygi2l9xazqrs">Soporte</p>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="flex items-center gap-1.5 text-sm text-[#2f6b4f] underline" data-id="element-104" data-mp="1tf2knqqfa0dm">
              <MailIcon className="h-4 w-4" data-id="element-105" data-mp="1kfp5qijj4jli" />
              {SUPPORT_EMAIL}
            </a>
          </div>
          <p className="mt-6 text-[11px] text-slate-400" data-id="element-106" data-mp="2h5evb9d1yb7w">
            © {new Date().getFullYear()} Kit RRHH en Orden · Chile. Todos los
            derechos reservados.
          </p>
        </Section>
      </main>
    </div>;
}